import PropTypes from "prop-types";
import { Fragment, useCallback, useMemo } from "react";

import { selectColumn } from "components/BaseTableV3/selectColumn";
import ACL_RELATIONSHIPS from "acl-relationships";
import BaseTableV3 from "components/BaseTableV3/BaseTableV3";
import { TableLink } from "components/BaseTableV3/TableLink";
import { getNextQueryParams } from "components/helpers";
import RuvixxDate from "components/RuvixxDate";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import TableIconBadge from "routes/custom_tables/TableIconBadge";
import { REGISTRANT_LEAD_STATUS_COLUMN } from "routes/leads/swoogo_registrants/registrantLeadStatusColumn";
import { REGISTRANT_LEAD_STATUS_FILTER } from "routes/leads/swoogo_registrants/registrantLeadStatusFilter";
import useCanFilterRegistrantLeads from "routes/leads/swoogo_registrants/useCanFilterRegistrantLeads";
import useCanReadRegistrantLeads from "routes/leads/swoogo_registrants/useCanReadRegistrantLeads";
import useCanSetRegistrantLeadStatus from "routes/leads/swoogo_registrants/useCanSetRegistrantLeadStatus";
import {
  EVENT_FILTER,
  omitScopedFilters,
  SESSION_FILTER,
} from "routes/swoogo/filterOptions";
import { linkCell } from "routes/swoogo/linkCell";
import { swoogoRegistrantKeys } from "routes/swoogo/queryKeys";
import RegistrantActions from "routes/swoogo/RegistrantActions";
import RegistrantBulkActions from "routes/swoogo/RegistrantBulkActions";
import useSwoogoConnected from "routes/swoogo/useSwoogoConnected";
import SwoogoService from "services/Swoogo";

const HEADER = "Swoogo Registrants";

const registrantCell = linkCell(({ id }) => `/swoogo/registrants/${id}`);

const renderDate = ({ row, column }) => {
  const date = row.getValue(column.id);
  return date ? <RuvixxDate date={date} /> : null;
};

const COLUMNS = [
  selectColumn,
  { id: "ID", accessorKey: "id", header: "ID", size: 90, cell: registrantCell },
  {
    id: "Name",
    accessorKey: "name",
    header: "Name",
    size: 200,
    cell: registrantCell,
  },
  { id: "Email", accessorKey: "email", header: "Email", size: 260 },
  {
    id: "Event",
    accessorKey: "event_name",
    sortKey: "event_id",
    header: "Event",
    size: 240,
    cell: linkCell(({ event_id }) => `/swoogo/events/${event_id}`),
  },
  {
    id: "Sessions",
    accessorKey: "sessions",
    header: "Sessions",
    size: 260,
    enableSorting: false,
    cell: ({ row }) =>
      row.original.sessions?.length ? (
        row.original.sessions.map((session, index) => (
          <Fragment key={session.id}>
            {index > 0 && ", "}
            <TableLink href={`/swoogo/sessions/${session.id}`}>
              {session.name}
            </TableLink>
          </Fragment>
        ))
      ) : (
        <span className="tw:text-[var(--color-text-muted)]">—</span>
      ),
  },
  {
    id: "Status",
    accessorKey: "registration_status",
    header: "Status",
    size: 140,
    enableSorting: false,
    cell: ({ row }) =>
      row.original.registration_status || (
        <span className="tw:text-[var(--color-text-muted)]">—</span>
      ),
  },
  {
    id: "Registered",
    accessorKey: "registered_at",
    header: "Registered",
    size: 160,
    cell: renderDate,
  },
  {
    id: "Checked In",
    accessorKey: "checked_in_at",
    header: "Checked In",
    size: 160,
    cell: renderDate,
  },
  {
    id: "Contact",
    accessorKey: "contact_name",
    header: "Contact",
    size: 200,
    enableSorting: false,
    cell: ({ row }) =>
      row.original.contact_id ? (
        <TableLink href={`/contacts/${row.original.contact_id}`}>
          {row.original.contact_name}
        </TableLink>
      ) : (
        <span className="tw:text-[var(--color-text-muted)]">Not linked</span>
      ),
  },
];

// The registration statuses that Swoogo documents.
const REGISTRATION_STATUSES = [
  "confirmed",
  "pending",
  "attended",
  "cancelled",
  "denied",
];

const FILTERS = [
  EVENT_FILTER,
  SESSION_FILTER,
  {
    key: "registration_status",
    title: "Status",
    type: "select",
    data: REGISTRATION_STATUSES.map(status => ({ id: status, name: status })),
  },
];

const RegistrantsTable = ({ scope }) => {
  usePageTitle(scope ? null : HEADER);

  const { lastSyncedAt } = useSwoogoConnected();
  const queryKey = useMemo(
    () => swoogoRegistrantKeys.list(lastSyncedAt),
    [lastSyncedAt]
  );

  const canLink = useAuthorized([ACL_RELATIONSHIPS.swoogo.linkContact]);
  const canEdit = useAuthorized([ACL_RELATIONSHIPS.swoogo.updateRegistrant]);
  const canRemoveSessionRegistrants = useAuthorized([
    ACL_RELATIONSHIPS.swoogo.removeSessionRegistrants,
  ]);
  const canReadLeads = useCanReadRegistrantLeads();
  const canSetLeadStatus = useCanSetRegistrantLeadStatus();
  const canFilterLeads = useCanFilterRegistrantLeads();

  const scopedSessionId = scope?.session_id;

  const filters = useMemo(
    () =>
      omitScopedFilters(
        canFilterLeads ? [...FILTERS, REGISTRANT_LEAD_STATUS_FILTER] : FILTERS,
        scope
      ),
    [canFilterLeads, scope]
  );

  const columns = useMemo(() => {
    let cols = COLUMNS;
    if (canReadLeads) {
      const statusIndex = COLUMNS.findIndex(column => column.id === "Status");
      cols = [
        ...COLUMNS.slice(0, statusIndex + 1),
        REGISTRANT_LEAD_STATUS_COLUMN,
        ...COLUMNS.slice(statusIndex + 1),
      ];
    }
    if (!(
      canLink ||
      canEdit ||
      canSetLeadStatus ||
      canRemoveSessionRegistrants
    )) {
      return cols;
    }
    return [
      ...cols,
      {
        header: <div />,
        id: "Actions",
        accessorKey: "actions",
        size: 60,
        enableSorting: false,
        enableResizing: false,
        meta: { isDisplay: true },
        cell: ({ row }) => (
          <RegistrantActions
            registrant={row.original}
            sessionId={scopedSessionId}
            canLink={canLink}
            canEdit={canEdit}
            canSetLeadStatus={canSetLeadStatus}
            canRemoveSessionRegistrants={canRemoveSessionRegistrants}
          />
        ),
      },
    ];
  }, [
    canReadLeads,
    canLink,
    canEdit,
    canSetLeadStatus,
    canRemoveSessionRegistrants,
    scopedSessionId,
  ]);

  const fetchRegistrants = useCallback(
    async queryParams =>
      SwoogoService.listRegistrants(getNextQueryParams(queryParams, columns)),
    [columns]
  );

  const renderBulkActions = useCallback(
    ({ selectedData, uncheckAll }) => (
      <RegistrantBulkActions
        selectedData={selectedData}
        uncheckAll={uncheckAll}
        sessionId={scopedSessionId}
        canRemoveSessionRegistrants={canRemoveSessionRegistrants}
        canSetLeadStatus={canSetLeadStatus}
      />
    ),
    [scopedSessionId, canRemoveSessionRegistrants, canSetLeadStatus]
  );

  return (
    <BaseTableV3
      columns={columns}
      headerIcon={<TableIconBadge iconName="user-check" />}
      header={HEADER}
      tableName="swoogo_registrants"
      queryKey={queryKey}
      fetchData={fetchRegistrants}
      filters={filters}
      extraQueryParams={scope}
      actionButtons={
        canRemoveSessionRegistrants || canSetLeadStatus
          ? renderBulkActions
          : undefined
      }
      enableSearch
      enableSettings
    />
  );
};

RegistrantsTable.propTypes = {
  scope: PropTypes.objectOf(PropTypes.number),
};

export default RegistrantsTable;
